import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation, useParams } from 'react-router-dom';
import './Login.css';
import logo from "../../assets/logo.svg";
import { startAuthentication } from '@simplewebauthn/browser';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFingerprint, faSpinner, faQrcode } from '@fortawesome/free-solid-svg-icons';
import LoginNavigationButton from './LoginNavigationButton';
import { buildApiUrl } from '../../config/api.config';

// Utility function to convert hex color to RGB for CSS rgba()
function hexToRgb(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ?
    `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` :
    '255, 255, 255';
}

const THEMES = {
  user: {
    title: "Diner Portal",
    subtitle: "Experience culinary excellence",
    quoteTitle: "Welcome to",
    quoteBrand: "Tastyflow",
    quoteDesc: "Crafting culinary excellence since 1995",
    color: "#ff4135",
    image: "https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?auto=format&fit=crop&w=1200&q=80"
  },
  staff: {
    title: "Staff Portal",
    subtitle: "Coordinate & Serve with Excellence",
    quoteTitle: "Team",
    quoteBrand: "Tastyflow",
    quoteDesc: "Empowering the heart of our hospitality.",
    color: "#00d1b2",
    image: "https://images.unsplash.com/photo-1600565193348-f74bd3c7ccdf?auto=format&fit=crop&w=1200&q=80"
  },
  admin: {
    title: "Management Portal",
    subtitle: "Control, Analyze & Scale",
    quoteTitle: "Executive",
    quoteBrand: "Tastyflow",
    quoteDesc: "Data-driven decisions for modern restaurants.",
    color: "#f39c12",
    image: "https://images.unsplash.com/photo-1551288049-bbbda536339a?auto=format&fit=crop&w=1200&q=80"
  }
};

const Login = (props) => {
  const { role: urlRole } = useParams();
  const role = urlRole || 'user';
  const theme = THEMES[role] || THEMES.user;
  const [credentials, setCredentials] = useState({
    email: "",
    password: ""
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [isAuthenticatingPasskey, setIsAuthenticatingPasskey] = useState(false);
  const [showTotpModal, setShowTotpModal] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [isAuthenticatingTotp, setIsAuthenticatingTotp] = useState(false);

  let navigate = useNavigate();
  const location = useLocation();
  const { email, password } = credentials;

  useEffect(() => {
    // Check for remembered email
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setCredentials(prev => ({ ...prev, email: savedEmail }));
      setRememberMe(true);
    }

    // Check for OAuth error in URL parameters
    const urlParams = new URLSearchParams(location.search);
    const error = urlParams.get('error');
    const email = urlParams.get('email');

    if (error) {
      props.showAlert(error, "danger");
      if (email) {
        setCredentials((prev) => ({ ...prev, email }));
      }
      // Clear the error and email from URL
      navigate('/login', { replace: true });
    }
  }, [location.search, navigate, props]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const response = await fetch(buildApiUrl('/users/login'), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        email,
        password
      }),
    });

    const json = await response.json();
    console.log(json);

    if (json.success) {
      localStorage.setItem('token', json.authtoken);
      
      // Save or remove remembered email
      if (rememberMe) {
        localStorage.setItem('rememberedEmail', email);
      } else {
        localStorage.removeItem('rememberedEmail');
      }
      
      // Fetch user details immediately to determine role
      try {
        const userRes = await fetch(buildApiUrl('/users/getuser'), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "auth-token": json.authtoken,
          },
        });
        const userData = await userRes.json();
        
        // --- Role Validation Logic ---
        const userRole = userData.role;
        let isRoleMatch = false;

        if (role === 'admin') {
          isRoleMatch = (userRole === 'admin' || userRole === 'owner');
        } else if (role === 'staff') {
          isRoleMatch = (userRole === 'staff');
        } else if (role === 'user') {
          isRoleMatch = (userRole === 'user');
        }

        if (!isRoleMatch) {
          localStorage.removeItem('token');
          localStorage.removeItem('userDetails');
          props.showAlert(`This account does not have ${role === 'user' ? 'Diner' : role.charAt(0).toUpperCase() + role.slice(1)} access. Please use the correct portal.`, "danger");
          return;
        }
        // -----------------------------

        localStorage.setItem("userDetails", JSON.stringify(userData));

        if (userData.role === 'owner') {
          // Redirect to standalone Master Portal with token
          window.location.href = `http://localhost:3001/dashboard?token=${json.authtoken}`;
        } else if (userData.role === 'admin') {
          // Set restaurant context for admin
          if (userData.restaurantId) {
             const rid = typeof userData.restaurantId === 'object' ? (userData.restaurantId._id || userData.restaurantId.id) : userData.restaurantId;
             localStorage.setItem('currentRestaurantId', rid);
          }
          navigate("/admin");
          props.showAlert("Admin Login Successful", "success");
        } else if (userData.role === 'staff' && userData.staffRole === 'manager') {
          // Setup restaurant context for the manager staff member
          if (userData.parentRestaurantId) {
             const rid = typeof userData.parentRestaurantId === 'object' ? (userData.parentRestaurantId._id || userData.parentRestaurantId.id) : userData.parentRestaurantId;
             localStorage.setItem('currentRestaurantId', rid);
          }
          navigate("/admin");
          props.showAlert("Manager Login Successful", "success");
        } else if (userData.role === 'staff' && userData.staffRole === 'chef') {
          // Setup restaurant context for the chef staff member
          if (userData.parentRestaurantId) {
             const rid = typeof userData.parentRestaurantId === 'object' ? (userData.parentRestaurantId._id || userData.parentRestaurantId.id) : userData.parentRestaurantId;
             localStorage.setItem('currentRestaurantId', rid);
          }
          navigate("/admin/kitchen");
          props.showAlert("Chef Login Successful", "success");
        } else {
          // Normal user: clear restaurant selection so they pick one every login
          localStorage.removeItem('currentRestaurantId');
          navigate("/");
          props.showAlert("Logged In Successfully", "success");
        }
      } catch (err) {
        console.error("Error fetching role:", err);
        navigate("/"); // Fallback
      }
    } else {
      props.showAlert(json.error || "Invalid Credentials", "danger");
    }
  };

  const handlePasskeyLogin = async () => {
    if (!email) {
      props.showAlert("Please enter your email first to use Passkey", "warning");
      return;
    }

    try {
      setIsAuthenticatingPasskey(true);
      const resp = await fetch(buildApiUrl('/security/webauthn/generate-authentication-options?email=${encodeURIComponent(email)}'));
      const data = await resp.json();

      if (!data.success) {
        props.showAlert(data.message || "No passkeys registered for this email", "danger");
        setIsAuthenticatingPasskey(false);
        return;
      }

      let asseResp;
      try {
        asseResp = await startAuthentication(data.options);
      } catch (error) {
        setIsAuthenticatingPasskey(false);
        return; // User cancelled
      }

      const verificationResp = await fetch(buildApiUrl('/security/webauthn/verify-authentication?email=${encodeURIComponent(email)}'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(asseResp)
      });
      const verificationData = await verificationResp.json();

      if (verificationData.success) {
        localStorage.setItem('token', verificationData.authtoken);
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }

        // Just like normal login, fetch user and route correctly
        try {
          const userRes = await fetch(buildApiUrl('/users/getuser'), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "auth-token": verificationData.authtoken,
            },
          });
          const userData = await userRes.json();
          
          const userRole = userData.role;
          let isRoleMatch = false;

          if (role === 'admin') {
            isRoleMatch = (userRole === 'admin' || userRole === 'owner');
          } else if (role === 'staff') {
            isRoleMatch = (userRole === 'staff');
          } else if (role === 'user') {
            isRoleMatch = (userRole === 'user');
          }

          if (!isRoleMatch) {
            localStorage.removeItem('token');
            localStorage.removeItem('userDetails');
            props.showAlert(`This account does not have ${role === 'user' ? 'Diner' : role.charAt(0).toUpperCase() + role.slice(1)} access. Please use the correct portal.`, "danger");
            return;
          }

          localStorage.setItem("userDetails", JSON.stringify(userData));

          if (userData.role === 'owner') {
            window.location.href = `http://localhost:3001/dashboard?token=${verificationData.authtoken}`;
          } else if (userData.role === 'admin') {
            if (userData.restaurantId) {
               const rid = typeof userData.restaurantId === 'object' ? (userData.restaurantId._id || userData.restaurantId.id) : userData.restaurantId;
               localStorage.setItem('currentRestaurantId', rid);
            }
            navigate("/admin");
            props.showAlert("Admin Login Successful", "success");
          } else if (userData.role === 'staff' && userData.staffRole === 'manager') {
            if (userData.parentRestaurantId) {
               const rid = typeof userData.parentRestaurantId === 'object' ? (userData.parentRestaurantId._id || userData.parentRestaurantId.id) : userData.parentRestaurantId;
               localStorage.setItem('currentRestaurantId', rid);
            }
            navigate("/admin");
            props.showAlert("Manager Login Successful", "success");
          } else if (userData.role === 'staff' && userData.staffRole === 'chef') {
            if (userData.parentRestaurantId) {
               const rid = typeof userData.parentRestaurantId === 'object' ? (userData.parentRestaurantId._id || userData.parentRestaurantId.id) : userData.parentRestaurantId;
               localStorage.setItem('currentRestaurantId', rid);
            }
            navigate("/admin/kitchen");
            props.showAlert("Chef Login Successful", "success");
          } else {
            localStorage.removeItem('currentRestaurantId');
            navigate("/");
            props.showAlert("Logged In Successfully", "success");
          }
        } catch (err) {
          console.error("Error fetching role:", err);
          navigate("/"); 
        }
      } else {
        props.showAlert(verificationData.error || verificationData.message || "Passkey Verification Failed", "danger");
      }
    } catch (error) {
      console.error(error);
      props.showAlert("Passkey Login Failed", "danger");
    } finally {
      setIsAuthenticatingPasskey(false);
    }
  };

  const submitTotpCode = async () => {
    if (totpCode.length !== 6) return;
    try {
      setIsAuthenticatingTotp(true);
      const resp = await fetch(buildApiUrl('/security/totp/authenticate'), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, token: totpCode })
      });
      const data = await resp.json();

      if (data.success) {
        localStorage.setItem('token', data.authtoken);
        if (rememberMe) {
          localStorage.setItem('rememberedEmail', email);
        } else {
          localStorage.removeItem('rememberedEmail');
        }

        try {
          const userRes = await fetch(buildApiUrl('/users/getuser'), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "auth-token": data.authtoken,
            },
          });
          const userData = await userRes.json();
          
          const userRole = userData.role;
          let isRoleMatch = false;

          if (role === 'admin') {
            isRoleMatch = (userRole === 'admin' || userRole === 'owner');
          } else if (role === 'staff') {
            isRoleMatch = (userRole === 'staff');
          } else if (role === 'user') {
            isRoleMatch = (userRole === 'user');
          }

          if (!isRoleMatch) {
            localStorage.removeItem('token');
            localStorage.removeItem('userDetails');
            props.showAlert(`This account does not have ${role === 'user' ? 'Diner' : role.charAt(0).toUpperCase() + role.slice(1)} access. Please use the correct portal.`, "danger");
            setShowTotpModal(false);
            setIsAuthenticatingTotp(false);
            return;
          }

          localStorage.setItem("userDetails", JSON.stringify(userData));

          if (userData.role === 'owner') {
            window.location.href = `${buildApiUrl(`/dashboard?token=${data.authtoken}`)}`;
          } else if (userData.role === 'admin') {
            if (userData.restaurantId) {
               const rid = typeof userData.restaurantId === 'object' ? (userData.restaurantId._id || userData.restaurantId.id) : userData.restaurantId;
               localStorage.setItem('currentRestaurantId', rid);
            }
            navigate("/admin");
            props.showAlert("Admin Login Successful", "success");
          } else if (userData.role === 'staff' && userData.staffRole === 'manager') {
            if (userData.parentRestaurantId) {
               const rid = typeof userData.parentRestaurantId === 'object' ? (userData.parentRestaurantId._id || userData.parentRestaurantId.id) : userData.parentRestaurantId;
               localStorage.setItem('currentRestaurantId', rid);
            }
            navigate("/admin");
            props.showAlert("Manager Login Successful", "success");
          } else if (userData.role === 'staff' && userData.staffRole === 'chef') {
            if (userData.parentRestaurantId) {
               const rid = typeof userData.parentRestaurantId === 'object' ? (userData.parentRestaurantId._id || userData.parentRestaurantId.id) : userData.parentRestaurantId;
               localStorage.setItem('currentRestaurantId', rid);
            }
            navigate("/admin/kitchen");
            props.showAlert("Chef Login Successful", "success");
          } else {
            localStorage.removeItem('currentRestaurantId');
            navigate("/");
            props.showAlert("Logged In Successfully", "success");
          }
        } catch (err) {
          console.error("Error fetching role:", err);
          navigate("/"); // Fallback
        }
      } else {
        props.showAlert(data.message || "Invalid or expired code", "danger");
      }
    } catch (error) {
      console.error(error);
      props.showAlert("Error authenticating", "danger");
    } finally {
      setIsAuthenticatingTotp(false);
      setTotpCode('');
      setShowTotpModal(false);
    }
  };

  const handleTotpInit = () => {
    if (!email) {
      props.showAlert("Please enter your email first to use Google Authenticator", "warning");
      return;
    }
    setShowTotpModal(true);
  };

  const onChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  return (
    <div
      className={`lgn-page-container theme-${role}`}
      style={{
        '--theme-color': theme.color,
        '--theme-color-rgb': hexToRgb(theme.color)
      }}
    >
      {/* Left side with restaurant image */}
      <div className='lgn-image-container' style={{ backgroundImage: `url(${theme.image})` }}>
        <div className='lgn-image-overlay'></div>
        <div className='lgn-restaurant-quote'>
          <h2>{theme.quoteTitle}</h2>
          <h1>{theme.quoteBrand}</h1>
          <p>{theme.quoteDesc}</p>
        </div>
      </div>
      
      {/* Right side with login form */}
      <div className='lgn-form-container'>
        {/* Restaurant logo */}
        <div className='lgn-logo-container'>
          <img src={logo} alt="TastyFlow" className='lgn-logo-image' />
        </div>

        {/* Portals Navigation Button */}
        <LoginNavigationButton theme={theme} />

        {/* Login form */}
        <div className='lgn-card'>
          <h1 className='lgn-heading'>{theme.title}</h1>
          <p className='lgn-subheading'>{theme.subtitle}</p>
          
          <form onSubmit={handleSubmit} className="lgn-form">
            <div className="lgn-form-group">
              <label htmlFor="email" className="lgn-form-label">Email Address</label>
              <input 
                type="email" 
                id="email" 
                name='email' 
                className="lgn-form-input" 
                onChange={onChange} 
                value={email} 
                placeholder="Enter your email"
              />
            </div>
            
            <div className="lgn-form-group">
              <label htmlFor="password" className="lgn-form-label">Password</label>
              <input 
                type="password" 
                id="password" 
                name='password' 
                className="lgn-form-input" 
                onChange={onChange} 
                value={password} 
                placeholder="••••••••"
              />
            </div>
            
            <div className="lgn-form-options">
              <div className="lgn-remember-me">
                <input 
                  type="checkbox" 
                  id="remember" 
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                />
                <label htmlFor="remember">Remember me</label>
              </div>
              <Link to="/forgot-password" className="lgn-forgot-password">Forgot Password?</Link>
            </div>
            
            <button type="submit" className="lgn-submit-btn">Sign In</button>

            {role === 'admin' && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', width: '100%', marginTop: '1rem' }}>
                  <button 
                    type="button" 
                    onClick={handlePasskeyLogin}
                    disabled={isAuthenticatingPasskey}
                    className="lgn-passkey-btn"
                    style={{ width: '100%', margin: 0 }}
                  >
                    {isAuthenticatingPasskey ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      <FontAwesomeIcon icon={faFingerprint} />
                    )}
                    {isAuthenticatingPasskey ? 'Verifying...' : 'Passkey'}
                  </button>
                  <button 
                    type="button" 
                    onClick={handleTotpInit}
                    disabled={isAuthenticatingTotp}
                    className="lgn-passkey-btn"
                    style={{ width: '100%', margin: 0 }}
                  >
                    <FontAwesomeIcon icon={faQrcode} />
                    Authenticator
                  </button>
              </div>
            )}

            <div className="lgn-divider">
              <span>or</span>
            </div>

            <button
              type="button"
              className="lgn-google-btn"
              onClick={() => window.location.href = buildApiUrl('/users/auth/google?origin=3000')}
            >
              <img
                src="https://developers.google.com/identity/images/g-logo.png"
                alt="Google"
                className="lgn-google-icon"
              />
              Continue with Google
            </button>

            <div className="lgn-signup-link">
              Don't have an account? <Link to={`/signup/${role}`} className="lgn-signup-link-text">Sign up</Link>
            </div>
          </form>
        </div>
      </div>
      
      {/* TOTP Code Modal */}
      {showTotpModal && (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(10px)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
            <div style={{
                background: '#1a1a2e', padding: '2.5rem', borderRadius: '24px',
                border: '1px solid rgba(255,255,255,0.1)', maxWidth: '400px', width: '90%',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1.5rem',
                boxShadow: '0 20px 40px rgba(0,0,0,0.4)'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: '800', color: 'white', margin: '0 0 0.5rem 0' }}>Authenticator Login</h3>
                    <p style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)', margin: 0, lineHeight: '1.4' }}>
                        Enter the 6-digit code from your authenticator app.
                    </p>
                </div>

                <div style={{ width: '100%' }}>
                    <input
                        type="text"
                        maxLength="6"
                        value={totpCode}
                        onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                        autoFocus
                        style={{
                            width: '100%', padding: '1rem', background: 'rgba(255,255,255,0.05)',
                            border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px',
                            color: 'white', fontSize: '1.5rem', letterSpacing: '8px', textAlign: 'center',
                            fontWeight: '800'
                        }}
                        placeholder="000000"
                    />
                </div>

                <div style={{ display: 'flex', gap: '1rem', width: '100%', marginTop: '0.5rem' }}>
                    <button onClick={() => { setShowTotpModal(false); setTotpCode(''); }} style={{ flex: 1, padding: '1rem', background: 'transparent', border: '1px solid rgba(255,255,255,0.1)', color: 'white', borderRadius: '12px', fontWeight: '800', cursor: 'pointer' }}>Cancel</button>
                    <button onClick={submitTotpCode} disabled={totpCode.length !== 6 || isAuthenticatingTotp} style={{ flex: 1, padding: '1rem', background: 'var(--tf-primary)', border: 'none', color: 'white', borderRadius: '12px', fontWeight: '800', cursor: totpCode.length === 6 ? 'pointer' : 'not-allowed', opacity: totpCode.length === 6 ? 1 : 0.5 }}>
                        {isAuthenticatingTotp ? 'Verifying...' : 'Verify'}
                    </button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default Login;
